/**
 * Diksha AI Assistant Chat Interface
 * Dedicated AI for Demo, Onboarding, and Tech Support
 * Accessed via link from Ayush
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Before/After Image Comparison Component
const BeforeAfterImages = ({ feature }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) return `${backendUrl}${url}`;
    return url;
  };
  
  return (
    <div className="mt-3 rounded-xl overflow-hidden border border-gray-200 bg-gray-50" data-testid="before-after-images">
      <div className="px-3 py-2 bg-purple-50 text-xs font-semibold text-purple-700 flex items-center gap-2">
        <span>🖥️ {feature.name_hi} — Live Screen</span>
      </div>
      <div className="grid grid-cols-2 gap-0">
        <div className="relative">
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium z-10">पहले</div>
          <img 
            src={getImageUrl(feature.before_image)} 
            alt="Before" 
            className="w-full h-40 object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <p className="text-xs text-gray-500 px-2 py-1 bg-red-50">{feature.before_text}</p>
        </div>
        <div className="relative border-l-2 border-purple-200">
          <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-medium z-10">शिक्षा मित्र ✨</div>
          <img 
            src={getImageUrl(feature.after_image)} 
            alt="After - शिक्षा मित्र" 
            className="w-full h-40 object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <p className="text-xs text-gray-600 px-2 py-1 bg-green-50">{feature.after_text}</p>
        </div>
      </div>
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ message, isUser, timestamp, feature }) => (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
    <div className={`max-w-[80%] ${isUser ? 'order-2' : 'order-1'}`}>
      {!isUser && (
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
            दी
          </div>
          <span className="text-sm font-medium text-purple-700">Diksha</span>
        </div>
      )}
      <div className={`rounded-2xl px-4 py-3 ${
        isUser 
          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-br-md' 
          : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
      }`}>
        <div className="whitespace-pre-wrap text-sm leading-relaxed" 
          dangerouslySetInnerHTML={{ __html: formatMessage(message) }} 
        />
        {!isUser && feature && <BeforeAfterImages feature={feature} />}
      </div>
      <p className={`text-xs mt-1 ${isUser ? 'text-right' : 'text-left'} text-gray-400`}>
        {timestamp}
      </p>
    </div>
  </div>
);

// Format message with markdown-like styling
const formatMessage = (text) => {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/##\s(.*?)(\n|$)/g, '<h3 class="font-bold text-lg mt-2 mb-1">$1</h3>')
    .replace(/###\s(.*?)(\n|$)/g, '<h4 class="font-semibold text-base mt-2 mb-1">$1</h4>')
    .replace(/---/g, '<hr class="my-2 border-gray-200">')
    .replace(/✅/g, '<span class="text-green-500">✅</span>')
    .replace(/❌/g, '<span class="text-red-500">❌</span>')
    .replace(/\n/g, '<br>');
};

// Feature Card Component
const FeatureCard = ({ feature, onClick }) => (
  <div 
    onClick={() => onClick(feature)}
    className="bg-white rounded-xl p-4 border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
  >
    <h4 className="font-bold text-gray-800">{feature.name}</h4>
    <p className="text-sm text-gray-500">{feature.name_hi}</p>
    <div className="mt-2 flex flex-wrap gap-1">
      {feature.key_benefits?.slice(0, 2).map((b, i) => (
        <span key={i} className="text-xs px-2 py-1 bg-purple-50 text-purple-600 rounded-full">
          {b}
        </span>
      ))}
    </div>
  </div>
);

// Quick Reply Button
const QuickReply = ({ text, onClick }) => (
  <button
    onClick={() => onClick(text)}
    className="px-4 py-2 bg-white border border-purple-200 rounded-full text-sm text-purple-700 hover:bg-purple-50 hover:border-purple-300 transition"
  >
    {text}
  </button>
);

// Main Chat Component
export default function DikshaChat() {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [features, setFeatures] = useState([]);
  const [showFeatures, setShowFeatures] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [sessionEnded, setSessionEnded] = useState(false);
  const messagesEndRef = useRef(null);
  const lastBotRef = useRef(null);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (sessionId && !loadedRef.current) {
      loadedRef.current = true;
      loadSession();
      loadFeatures();
    }
  }, [sessionId]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last) return;
    const t = setTimeout(() => {
      if (last.isUser) {
        // user's own message → keep view at the bottom
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else {
        // Diksha's reply → align the START of her message to the top so the user
        // reads it from the beginning (no jump past long answers / focus loss).
        lastBotRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/diksha/session/${sessionId}`);
      const data = await res.json();
      
      if (data.success) {
        setSession(data.session);
        
        if (data.session.status === 'completed' || data.session.status === 'abandoned') {
          setSessionEnded(true);
        } else {
          // Add intro message
          addMessage({
            text: `🙏 नमस्ते ${data.session.client_name}! मैं **दीक्षा** हूं।\n\nमैं आपकी मदद करने के लिए तैयार हूं:\n\n📱 **Demo** - Platform features देखें\n🚀 **Onboarding** - School setup में सहायता\n🔧 **Tech Support** - Problems solve करें\n\nआप किस चीज़ में interested हैं?`,
            isUser: false
          });
          setQuickReplies(['Demo देखें', 'Onboarding Help', 'Technical Issue']);
        }
      }
    } catch (err) {
      console.error('Error loading session:', err);
    }
    setLoading(false);
  };

  const loadFeatures = async () => {
    try {
      const res = await fetch(`${API_URL}/api/diksha/features`);
      const data = await res.json();
      if (data.success) {
        setFeatures(data.features);
      }
    } catch (err) {
      console.error('Error loading features:', err);
    }
  };

  const addMessage = ({ text, isUser, feature }) => {
    const newMessage = {
      id: Date.now(),
      text,
      isUser,
      feature: feature || null,
      timestamp: new Date().toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || sending || sessionEnded) return;

    const userMessage = messageText.trim();
    addMessage({ text: userMessage, isUser: true });
    setInputValue('');
    setSending(true);
    setQuickReplies([]);

    try {
      const res = await fetch(`${API_URL}/api/diksha/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage
        })
      });

      const data = await res.json();
      
      if (data.success) {
        addMessage({ 
          text: data.response, 
          isUser: false,
          feature: data.demo_content || null
        });
        
        if (data.next_suggestions) {
          setQuickReplies(data.next_suggestions);
        }

        if (data.demo_content) {
          setShowFeatures(true);
        }
      } else if (data.new_session_required) {
        setSessionEnded(true);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      addMessage({ text: 'Sorry, कुछ error हो गई। कृपया फिर से try करें।', isUser: false });
    }

    setSending(false);
  };

  const handleFeatureClick = (feature) => {
    sendMessage(`मुझे ${feature.name} के बारे में बताओ`);
    setShowFeatures(false);
  };

  const handleQuickReply = (text) => {
    sendMessage(text);
  };

  const endSession = async () => {
    try {
      await fetch(`${API_URL}/api/diksha/session/${sessionId}/end`, {
        method: 'POST'
      });
      setSessionEnded(true);
      addMessage({ text: '🙏 धन्यवाद! आपका session end हो गया है। कोई और मदद चाहिए तो Ayush से नया link लें।', isUser: false });
    } catch (err) {
      console.error('Error ending session:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 animate-pulse">
            दी
          </div>
          <p className="text-purple-600">Loading Diksha...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="text-center p-8">
          <span className="text-5xl">😔</span>
          <h2 className="text-xl font-bold text-gray-700 mt-4">Session Not Found</h2>
          <p className="text-gray-500 mt-2">यह link valid नहीं है। Ayush से नया link लें।</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex flex-col" data-testid="diksha-chat">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
              दी
            </div>
            <div>
              <h1 className="font-bold text-lg">Diksha</h1>
              <p className="text-purple-200 text-sm">AI Demo & Support Assistant</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs ${
              session.mode === 'demo' ? 'bg-blue-500' :
              session.mode === 'onboarding' ? 'bg-green-500' : 'bg-orange-500'
            }`}>
              {session.mode === 'demo' ? '📱 Demo' :
               session.mode === 'onboarding' ? '🚀 Onboarding' : '🔧 Support'}
            </span>
            
            {!sessionEnded && (
              <button
                onClick={endSession}
                className="px-3 py-1 bg-white/20 rounded-full text-xs hover:bg-white/30"
              >
                End Session
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="flex-1 max-w-4xl w-full mx-auto p-4 flex flex-col">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-2 pb-4">
          {messages.map((msg, idx) => {
            const isLast = idx === messages.length - 1;
            return (
              <div key={msg.id} ref={isLast && !msg.isUser ? lastBotRef : null} className="scroll-mt-2">
                <MessageBubble
                  message={msg.text}
                  isUser={msg.isUser}
                  timestamp={msg.timestamp}
                  feature={msg.feature}
                />
              </div>
            );
          })}
          
          {sending && (
            <div className="flex items-center gap-2 text-purple-500">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm animate-pulse">
                दी
              </div>
              <span className="text-sm">Diksha typing...</span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Features Grid */}
        {showFeatures && (
          <div className="mb-4">
            <h3 className="font-semibold text-gray-700 mb-2">🎯 Platform Features</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {features.slice(0, 8).map(feature => (
                <FeatureCard
                  key={feature.id}
                  feature={feature}
                  onClick={handleFeatureClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Quick Replies */}
        {quickReplies.length > 0 && !sessionEnded && (
          <div className="flex flex-wrap gap-2 mb-4">
            {quickReplies.map((reply, idx) => (
              <QuickReply
                key={idx}
                text={reply}
                onClick={handleQuickReply}
              />
            ))}
          </div>
        )}

        {/* Session Ended Message */}
        {sessionEnded && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 text-center">
            <span className="text-2xl">🙏</span>
            <p className="text-yellow-800 font-medium mt-2">Session Ended</p>
            <p className="text-yellow-600 text-sm">नया session शुरू करने के लिए Ayush से link लें</p>
          </div>
        )}

        {/* Input Area */}
        {!sessionEnded && (
          <div className="bg-white rounded-2xl shadow-lg border p-2 flex items-center gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage(inputValue)}
              placeholder="अपना message लिखें..."
              disabled={sending}
              className="flex-1 px-4 py-3 outline-none text-gray-700"
            />
            <button
              onClick={() => sendMessage(inputValue)}
              disabled={sending || !inputValue.trim()}
              className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white flex items-center justify-center disabled:opacity-50"
            >
              {sending ? '⏳' : '➤'}
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-2 text-xs text-gray-400">
        Powered by Shiksha Mitra AI
      </div>
    </div>
  );
}
